/**
 * Operations API client (backs the consolidated Operations page).
 *
 * Talks to /api/v1/platform/triage/* — the Explorer (summary tiles, operator
 * rows, encounter rows) plus the per-operator and per-encounter drill-downs.
 */

import { apiService } from './api';

export type TriageSeverity = 'info' | 'warning' | 'blocker';

export interface TriageFinding {
  ruleId: string;
  severity: TriageSeverity;
  title: string;
  explanation: string;
  suggestedNextStep: string;
  evidence?: Record<string, unknown>;
}

export interface TriageNoteRow {
  noteId: string;
  status: string | null;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  encounterId: string | null;
  appointmentId: string | null;
  patientId: string | null;
  patientMrn: string | null;
}

export interface TriageEncounterRow {
  encounterId: string;
  status: string | null;
  signedAt: string | null;
  createdAt: string;
  isDeleted: boolean;
  ehrDeliveryStatus: string | null;
  ehrDeliveredAt: string | null;
  patientId: string | null;
  patientMrn: string | null;
}

export interface TriageConnectedEncounterRow {
  encounterId: string;
  clinicalNoteStatus: string | null;
  retentionExpiresAt: string | null;
  isDeleted: boolean;
  patientId: string | null;
  patientMrn: string | null;
  retentionDaysRemaining: number | null;
}

export type RetentionPhase =
  | 'active'
  | 'soft-deleted-recoverable'
  | 'hard-delete-imminent'
  | 'expired';

export interface TriageRecordingRow {
  recordingId: string;
  status: string | null;
  encounterId: string | null;
  appointmentId: string | null;
  durationSeconds: number | null;
  createdAt: string;
  recordedAt: string | null;
  isDeleted: boolean;
  deletedAt: string | null;
  retentionExpiresAt: string | null;
  retentionNotified5Day: boolean;
  retentionNotified1Day: boolean;
  patientId: string | null;
  patientMrn: string | null;
  storage: {
    provider: string | null;
    containerName: string | null;
    blobName: string | null;
    sizeBytes: number | null;
    uploadedAt: string | null;
  };
  softDeleteAt: string | null;
  hardDeleteAt: string | null;
  daysUntilSoftDelete: number | null;
  daysUntilHardDelete: number | null;
  retentionPhase: RetentionPhase | null;
}

export interface TriageReport {
  user: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    organizationId: string | null;
    organizationName: string | null;
    isActive: boolean;
    isDeleted: boolean;
    account: {
      lastLogin: string | null;
      lockUntil: string | null;
      isLocked: boolean;
      loginAttempts: number;
      passwordResetToken: boolean;
      passwordResetExpires: string | null;
      lastPasswordChange: string | null;
    };
  };
  clinical: {
    notes: TriageNoteRow[];
    clinicalEncounters: TriageEncounterRow[];
    connectedEncounters: TriageConnectedEncounterRow[];
    recordings: TriageRecordingRow[];
    counts: {
      notesTotal: number;
      notesSigned: number;
      notesDraft: number;
      notesPending: number;
      encountersTotal: number;
      encountersSigned: number;
      encountersActive: number;
      recordingsTotal: number;
      recordingsActive: number;
      recordingsSoftDeleted: number;
      recordingsExpiringSoon: number;
    };
  };
  findings: TriageFinding[];
  generatedAt: string;
  meta: { phiLevel: string; readOnly: boolean; truncated: boolean };
}

export interface TriageEncounterReport {
  encounter: {
    clinicalEncounterId: string;
    rawStatus: string | null;
    derivedStatus: string;
    createdAt: string;
    updatedAt: string;
    isDeleted: boolean;
    signedAt: string | null;
    ehrDeliveryStatus: string | null;
    ehrDeliveredAt: string | null;
    retentionExpiresAt: string | null;
    appointmentId: string | null;
    encounterId: string | null;
    organizationId: string | null;
    organizationName: string | null;
    patientId: string | null;
    patientMrn: string | null;
    transcriptCount: number;
  };
  provider: { userId: string | null; name: string | null; email: string | null };
  notes: TriageNoteRow[];
  recordings: TriageRecordingRow[];
  findings: TriageFinding[];
  generatedAt: string;
  meta: { phiLevel: string; readOnly: boolean };
}

// --- Operations Explorer -----------------------------------------------------

/** Enterprise→Partner→Org scope passed as query params. */
export interface OperationsScope {
  enterpriseId?: string;
  partnerId?: string;
  organizationId?: string;
}

export interface OperationsFunnelStage {
  stage: string;
  label: string;
  count: number;
  conversionFromPrev: number;
}

export interface OperationsSummary {
  windowDays: number;
  encounters: number;
  signed: number;
  conversionPercent: number;
  timeToSignP50Minutes: number | null;
  timeToSignP95Minutes: number | null;
  stuckCount: number;
  activeOperators: number;
  funnelStages: OperationsFunnelStage[];
  orgHealth: { green: number; yellow: number; red: number; idle: number };
}

export interface OperatorStatsRow {
  userId: string;
  name: string | null;
  email: string;
  organizationId: string | null;
  organizationName: string | null;
  encountersInWindow: number;
  stuckCount: number;
  signedCount: number;
  completionRate: number;
  p50TimeToSignMinutes: number | null;
  lastActiveAt: string | null;
}

export interface EncounterExplorerRow {
  encounterId: string;
  patientId: string | null;
  patientMrn: string | null;
  providerName: string | null;
  organizationName: string | null;
  rawStatus: string | null;
  derivedStatus: string;
  createdAt: string;
  updatedAt: string;
  ageMinutes: number;
  isStuck: boolean;
  retentionExpiresAt: string | null;
  ehrDeliveryStatus: string | null;
  recordingDurationSeconds: number | null;
}

export interface ScopeNode {
  id: string;
  name: string;
}
export interface ScopePartner extends ScopeNode {
  organizations: ScopeNode[];
}
export interface ScopeEnterprise extends ScopeNode {
  partners: ScopePartner[];
}
export interface ScopeHierarchy {
  enterprises: ScopeEnterprise[];
  orphanPartners: ScopePartner[];
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  error?: string;
  meta?: Record<string, unknown>;
}

/** Drop undefined/'all' scope values so they aren't sent as query params. */
function scopeParams(scope: OperationsScope): Record<string, string> {
  const out: Record<string, string> = {};
  if (scope.enterpriseId && scope.enterpriseId !== 'all') out.enterpriseId = scope.enterpriseId;
  if (scope.partnerId && scope.partnerId !== 'all') out.partnerId = scope.partnerId;
  if (scope.organizationId && scope.organizationId !== 'all') out.organizationId = scope.organizationId;
  return out;
}

class TriageService {
  async getScopeHierarchy(): Promise<ScopeHierarchy> {
    const res = await apiService.get<ApiEnvelope<ScopeHierarchy>>(
      '/v1/platform/triage/scope-hierarchy',
    );
    return res.data;
  }

  async getOperationsSummary(scope: OperationsScope, windowDays: number): Promise<OperationsSummary> {
    const res = await apiService.get<ApiEnvelope<OperationsSummary>>(
      '/v1/platform/triage/summary',
      { ...scopeParams(scope), windowDays },
    );
    return res.data;
  }

  async getOperators(scope: OperationsScope, windowDays: number, q?: string): Promise<OperatorStatsRow[]> {
    const params: Record<string, unknown> = { ...scopeParams(scope), windowDays };
    if (q && q.trim().length >= 2) params.q = q.trim();
    const res = await apiService.get<ApiEnvelope<OperatorStatsRow[]>>(
      '/v1/platform/triage/operators',
      params,
    );
    return res.data ?? [];
  }

  async getEncounters(
    scope: OperationsScope,
    opts: { windowDays?: number; providerId?: string } = {},
  ): Promise<EncounterExplorerRow[]> {
    const params: Record<string, unknown> = { ...scopeParams(scope) };
    if (opts.windowDays) params.windowDays = opts.windowDays;
    if (opts.providerId) params.providerId = opts.providerId;
    const res = await apiService.get<ApiEnvelope<EncounterExplorerRow[]>>(
      '/v1/platform/triage/encounters',
      params,
    );
    return res.data ?? [];
  }

  async getUserReport(userId: string): Promise<TriageReport> {
    const res = await apiService.get<ApiEnvelope<TriageReport>>(
      `/v1/platform/triage/user/${userId}`,
    );
    return res.data;
  }

  async getEncounterReport(encounterId: string): Promise<TriageEncounterReport> {
    const res = await apiService.get<ApiEnvelope<TriageEncounterReport>>(
      `/v1/platform/triage/encounter/${encounterId}`,
    );
    return res.data;
  }
}

export const triageService = new TriageService();
